import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Link2, Unlink, Hash, Users, Loader2, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/utils/constants";

export const SlackSettingsView = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [slackUsers, setSlackUsers] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/slack/status`);
      setStatus(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const connectSlack = async () => {
    try {
      const r = await axios.get(`${API}/slack/connect-url`);
      window.open(r.data.url, '_blank');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate Slack connect URL");
    }
  };

  const disconnectSlack = async () => {
    try {
      await axios.delete(`${API}/slack/disconnect`);
      toast.success("Slack disconnected");
      setStatus({ connected: false });
      setChannels([]);
    } catch { toast.error("Failed to disconnect"); }
  };

  const loadChannels = async () => {
    setLoadingChannels(true);
    try {
      const r = await axios.get(`${API}/slack/channels`);
      setChannels(r.data);
    } catch { toast.error("Failed to load channels"); }
    setLoadingChannels(false);
  };

  const setDefaultChannel = async (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    if (!channel) return;
    try {
      await axios.post(`${API}/slack/set-channel`, { channel_id: channelId, channel_name: channel.name });
      toast.success(`Default channel set to #${channel.name}`);
    } catch { toast.error("Failed to set channel"); }
  };

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="slack-settings-title">
          <MessageSquare className="h-5 w-5 text-primary" />Slack Integration
        </h2>
        <p className="text-sm text-muted-foreground">Connect Slack to receive task notifications as DMs and channel messages</p>
        <Badge variant="outline" className="mt-2 text-xs">Pro Feature</Badge>
      </div>

      <div className="bg-card rounded-xl border p-6">
        {status?.connected ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Connected to {status.team_name}</div>
                  <div className="text-xs text-muted-foreground">Workspace linked</div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={disconnectSlack} className="text-destructive">
                <Unlink className="h-4 w-4 mr-1.5" />Disconnect
              </Button>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Hash className="h-4 w-4" />Default Team Channel
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Overdue task summaries will be posted to this channel.</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadChannels} disabled={loadingChannels}>
                  {loadingChannels ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Hash className="h-4 w-4 mr-1.5" />}
                  Load Channels
                </Button>
                {channels.length > 0 && (
                  <Select onValueChange={setDefaultChannel}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select a channel" /></SelectTrigger>
                    <SelectContent>
                      {channels.map(c => <SelectItem key={c.id} value={c.id}>#{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-2">Notification Triggers</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" />Task assignment → Slack DM to assignee</div>
                <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" />Task overdue → Slack DM to assignee + team channel</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
            <h3 className="font-semibold mb-2">Connect Your Slack Workspace</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Get task assignments as DMs and overdue alerts in your team channel. Your team stays in sync without leaving Slack.
            </p>
            <Button onClick={connectSlack} className="gap-2" data-testid="connect-slack-btn">
              <ExternalLink className="h-4 w-4" />Add to Slack
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
